import { useEffect, useMemo, useRef, useState } from "react";
import {
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { AlertCircle, Loader2, RefreshCw, User } from "lucide-react";
import { toast } from "sonner";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { formatPlainDate } from "@/common/utils/format-plain-date";
import { AFFILIATION_END_REASON_LABELS } from "@/features/affiliation/affiliation-end-reason-labels";
import { ChatDialog } from "@/features/chat/chat-dialog";
import { ProviderCredentialsSheetV2 } from "@/routes/_org/org/bu/$organizationBusinessUnitId/providers/credentials/_components/provider-credentials-sheet-v2";
import { renderWorkspaceRunActivity } from "@/features/provider-workspace/psv-run-activity";
import { useTRPC, useTRPCClient } from "@/trpc";
import { EndedProviderActionsMenu } from "./ended-provider-actions-menu";
import {
  getEndedFromStatusLabel,
  type EndedProviderRow,
} from "./ended-provider-types";
import { ReinstateProviderDialog } from "./reinstate-provider-dialog";

const ENDED_PROVIDERS_PAGE_SIZE = 100;
const LOAD_MORE_ROOT_MARGIN = "300px";

function EndedBadge() {
  return (
    <Badge
      variant="outline"
      className="border-slate-300 bg-slate-100 text-slate-700"
    >
      Ended
    </Badge>
  );
}

function endReasonLabel(row: EndedProviderRow): string {
  return AFFILIATION_END_REASON_LABELS[row.endReason];
}

export function EndedProvidersList({
  organizationBusinessUnitId,
  search,
  specialtyCode,
  assignedToUserId,
}: {
  organizationBusinessUnitId: string;
  search: string;
  specialtyCode: string;
  assignedToUserId: string | undefined;
}) {
  const trpc = useTRPC();
  const trpcClient = useTRPCClient();
  const queryClient = useQueryClient();
  const loadMoreRef = useRef<HTMLDivElement>(null);
  const [chattingRow, setChattingRow] = useState<EndedProviderRow | null>(null);
  const [credentialsRow, setCredentialsRow] = useState<EndedProviderRow | null>(
    null,
  );
  const [reinstateRow, setReinstateRow] = useState<EndedProviderRow | null>(
    null,
  );

  const listInput = useMemo(
    () => ({
      organizationBusinessUnitId,
      search: search || undefined,
      specialtyCode: specialtyCode || undefined,
      assignedToUserId,
    }),
    [assignedToUserId, organizationBusinessUnitId, search, specialtyCode],
  );

  const {
    data,
    isLoading,
    isError,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: [
      ...trpc.organizationBusinessUnitAffiliation.listEnded.pathKey(),
      "infinite",
      listInput,
    ],
    queryFn: ({ pageParam }) =>
      trpcClient.organizationBusinessUnitAffiliation.listEnded.query({
        ...listInput,
        limit: ENDED_PROVIDERS_PAGE_SIZE,
        offset: pageParam * ENDED_PROVIDERS_PAGE_SIZE,
      }),
    getNextPageParam: (lastPage, allPages) => {
      const loadedCount = allPages.reduce(
        (count, page) => count + page.rows.length,
        0,
      );
      return loadedCount < lastPage.total ? allPages.length : undefined;
    },
    initialPageParam: 0,
  });

  const rows = useMemo(
    () => data?.pages.flatMap((page) => page.rows) ?? [],
    [data],
  );
  const primarySourceVerificationEnabled =
    data?.pages[0]?.primarySourceVerificationEnabled ?? false;

  useEffect(() => {
    const node = loadMoreRef.current;
    if (!node) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && hasNextPage && !isFetchingNextPage) {
          void fetchNextPage();
        }
      },
      { rootMargin: LOAD_MORE_ROOT_MARGIN },
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [fetchNextPage, hasNextPage, isFetchingNextPage, rows.length]);

  const reinstateMutation = useMutation(
    trpc.organizationBusinessUnitAffiliation.reinstate.mutationOptions({
      onSuccess: async () => {
        const name = reinstateRow?.name;
        setReinstateRow(null);
        await Promise.all([
          queryClient.invalidateQueries({
            queryKey:
              trpc.organizationBusinessUnitAffiliation.listEnded.pathKey(),
          }),
          queryClient.invalidateQueries({
            queryKey: trpc.profile.list.queryKey(),
          }),
          queryClient.invalidateQueries({ queryKey: ["profileListInfinite"] }),
        ]);
        toast.success(
          name ? `${name} was reinstated.` : "Provider reinstated.",
        );
      },
      onError: (mutationError) => {
        toast.error(mutationError.message || "Failed to reinstate provider.");
      },
    }),
  );

  const confirmReinstate = () => {
    if (!reinstateRow) {
      return;
    }
    reinstateMutation.mutate({
      organizationBusinessUnitId,
      endedAffiliationId: reinstateRow.endedAffiliationId,
    });
  };

  const renderActions = (row: EndedProviderRow) => (
    <EndedProviderActionsMenu
      row={row}
      onChat={() => setChattingRow(row)}
      onViewCredentials={() => setCredentialsRow(row)}
      onReinstate={() => setReinstateRow(row)}
    />
  );

  let content;
  if (isLoading) {
    content = (
      <Card className="border-dashed">
        <CardContent className="p-6 sm:p-8 md:p-12 flex flex-col items-center justify-center text-center">
          <Loader2 className="h-8 w-8 text-blue-600 animate-spin mb-4" />
          <h3 className="text-lg sm:text-xl font-semibold">
            Loading ended relationships
          </h3>
        </CardContent>
      </Card>
    );
  } else if (isError) {
    content = (
      <Card className="border-dashed border-2 border-red-300 bg-red-50">
        <CardContent className="p-6 sm:p-8 md:p-12 flex flex-col items-center justify-center text-center">
          <AlertCircle className="h-8 w-8 text-red-600 mb-4" />
          <h3 className="text-lg sm:text-xl font-semibold mb-2">
            Failed to load ended relationships
          </h3>
          <p className="text-gray-600 mb-6 max-w-md text-sm sm:text-base">
            {error instanceof Error ? error.message : "Please try again."}
          </p>
          <Button
            className="bg-blue-600 hover:bg-blue-700"
            onClick={() => void refetch()}
          >
            <RefreshCw className="mr-2 h-4 w-4" />
            Retry
          </Button>
        </CardContent>
      </Card>
    );
  } else if (rows.length === 0) {
    content = (
      <Card className="border-dashed">
        <CardContent className="p-6 sm:p-8 md:p-12 flex flex-col items-center justify-center text-center">
          <div className="bg-blue-100 p-3 rounded-full mb-4">
            <User className="h-8 w-8 text-blue-600" />
          </div>
          <h3 className="text-lg sm:text-xl font-semibold">
            No ended relationships
          </h3>
        </CardContent>
      </Card>
    );
  } else {
    content = (
      <>
        {/* Card Layout (mobile + tablet) */}
        <div className="block lg:hidden space-y-4 min-w-0">
          {rows.map((row) => (
            <Card
              key={row.endedAffiliationId}
              className="rounded-2xl border border-slate-200/80 bg-white shadow-sm"
            >
              <CardContent className="space-y-3 p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3 flex-1 min-w-0">
                    <Avatar className="h-12 w-12 flex-shrink-0">
                      <AvatarFallback className="bg-gray-200">
                        <User className="h-6 w-6 text-gray-500" />
                      </AvatarFallback>
                    </Avatar>
                    <div className="w-0 flex-1">
                      <p className="truncate text-lg font-semibold text-slate-900">
                        {row.name}
                      </p>
                      <div className="mt-1 flex flex-wrap gap-1">
                        <EndedBadge />
                        <Badge variant="outline">
                          Was {getEndedFromStatusLabel(row.endedFromStatus)}
                        </Badge>
                      </div>
                      <div className="mt-2 space-y-1 text-sm text-slate-600">
                        <p>{row.role}</p>
                        <p>{row.specialty || "No specialty"}</p>
                        <p>
                          Ended {formatPlainDate(row.endDate)} ·{" "}
                          {endReasonLabel(row)}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="flex-shrink-0">{renderActions(row)}</div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Desktop Table Layout */}
        <Card className="hidden lg:block p-0 border border-slate-200/80 shadow-sm overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50 text-slate-600 uppercase tracking-wide text-xs">
                <TableHead className="w-12"></TableHead>
                <TableHead className="min-w-[150px]">Name</TableHead>
                <TableHead className="min-w-[120px]">Provider type</TableHead>
                <TableHead className="min-w-[120px]">Specialty</TableHead>
                <TableHead>Previous status</TableHead>
                <TableHead>Ended</TableHead>
                <TableHead>Reason</TableHead>
                <TableHead className="w-[200px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row) => (
                <TableRow
                  key={row.endedAffiliationId}
                  className="hover:bg-slate-50"
                >
                  <TableCell className="py-3">
                    <Avatar className="h-10 w-10">
                      <AvatarFallback className="bg-gray-200">
                        <User className="h-5 w-5 text-gray-500" />
                      </AvatarFallback>
                    </Avatar>
                  </TableCell>
                  <TableCell className="py-3">
                    <p className="max-w-[200px] truncate font-semibold text-slate-900">
                      {row.name}
                    </p>
                    <div className="mt-1">
                      <EndedBadge />
                    </div>
                  </TableCell>
                  <TableCell className="py-3 text-sm">{row.role}</TableCell>
                  <TableCell className="py-3 text-sm">
                    {row.specialty || "No specialty"}
                  </TableCell>
                  <TableCell className="py-3 text-sm">
                    {getEndedFromStatusLabel(row.endedFromStatus)}
                  </TableCell>
                  <TableCell className="py-3 text-sm">
                    {formatPlainDate(row.endDate)}
                  </TableCell>
                  <TableCell className="py-3 text-sm">
                    {endReasonLabel(row)}
                  </TableCell>
                  <TableCell className="py-3 text-right">
                    {renderActions(row)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
        <div
          ref={loadMoreRef}
          className="flex min-h-10 items-center justify-center py-4 text-sm text-slate-500"
        >
          {isFetchingNextPage && (
            <span className="inline-flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading more providers
            </span>
          )}
          {!isFetchingNextPage &&
            hasNextPage &&
            "Scroll to load more providers"}
          {!isFetchingNextPage &&
            !hasNextPage &&
            "All matching providers loaded"}
        </div>
      </>
    );
  }

  return (
    <>
      {content}
      {chattingRow && (
        <ChatDialog
          isOpen
          onOpenChange={() => setChattingRow(null)}
          user={{
            id: chattingRow.providerUserId,
            firstName: chattingRow.firstName,
            lastName: chattingRow.lastName,
          }}
        />
      )}
      {credentialsRow?.credentialViewId && (
        <ProviderCredentialsSheetV2
          provider={{
            id: credentialsRow.providerUserId,
            firstName: credentialsRow.firstName ?? "",
            lastName: credentialsRow.lastName ?? "",
            role: credentialsRow.role ?? "",
            specialty: credentialsRow.specialty ?? "",
            isRevealed: true,
            documents: [],
          }}
          providerUserId={credentialsRow.providerUserId}
          credentialViewId={credentialsRow.credentialViewId}
          organizationBusinessUnitId={organizationBusinessUnitId}
          primarySourceVerificationEnabled={primarySourceVerificationEnabled}
          // The frozen record is read-only: its PSV history and item
          // decisions are shown, but no new verification can be started.
          renderRunActivity={renderWorkspaceRunActivity}
          open
          onOpenChange={(open) => {
            if (!open) {
              setCredentialsRow(null);
            }
          }}
        />
      )}
      <ReinstateProviderDialog
        target={reinstateRow}
        isPending={reinstateMutation.isPending}
        onCancel={() => setReinstateRow(null)}
        onConfirm={confirmReinstate}
      />
    </>
  );
}
